import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { DropdownItem, PremiumRule, ageOnToday, dobWindow } from './member.model';
import { DigitsOnlyDirective } from '../../shared/digits-only';
import { TermsService } from '../../services/terms';

/**
 * Step 1 (Premium & Member Details) of the member-onboarding wizard.
 *
 * Presentational child: the parent stepper builds the ROOT FormGroup (with all
 * validators), fetches the premium rules and owns the rule-driven premium
 * preview. This component renders the rule <select>, the read-only policy +
 * premium summary, and the Step-1 member fields, plus owns the two purely
 * derived fields — FullName (auto-concat) and Age (from DOB) — and keeps PAN
 * uppercased as the user types.
 */
@Component({
  selector: 'app-member-details-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    DatePickerModule,
    InputTextModule,
    CheckboxModule,
    CardModule,
    DialogModule,
    ButtonModule,
    DigitsOnlyDirective,
  ],
  templateUrl: './member-details-step.html',
  styleUrls: ['./member-details-step.css'],
})
export class MemberDetailsStep implements OnInit, OnChanges, OnDestroy {
  private terms = inject(TermsService);
  private sanitizer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);

  /** Active Terms & Conditions (rendered HTML) shown in the "View T&C" dialog. */
  showTerms = signal(false);
  termsHtml = signal<SafeHtml | null>(null);
  private termsLoaded = false;
  /** The ROOT form, already built (with controls + validators) by the parent. */
  @Input({ required: true }) form!: FormGroup;
  /** Options for the premium-rule select (value = PremiumRuleId). */
  @Input() premiumRuleOptions: DropdownItem[] = [];
  /** String options for the Gender select. */
  @Input() genderOptions: string[] = [];
  /** The selected premium rule — used to show the allowed age range. */
  @Input() selectedRule: PremiumRule | null = null;
  /** Shown read-only only when present (post-save). */
  @Input() memberUniqueId: string | null = null;

  /** DatePicker maxDate when no rule is chosen yet — no future dates of birth. */
  readonly today = new Date();

  /**
   * The calendar is BOUNDED by the selected plan's age band, so an ineligible date cannot be
   * picked at all.
   *
   * Held as fields recomputed in ngOnChanges, NOT getters: a getter returning `new Date()` hands
   * the datepicker a fresh object reference on every change-detection pass, which is a needless
   * re-render of the calendar on every keystroke elsewhere in the form.
   */
  dobMin?: Date;
  dobMax: Date = this.today;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['selectedRule']) return;
    const r = this.selectedRule;
    const w = r ? dobWindow(r.ageFrom, r.ageTo) : null;
    this.dobMin = w?.min;
    this.dobMax = w?.max ?? this.today;
  }

  /** Age as it stands right now — recomputed from the control, so it is never stale. */
  get currentAge(): number | null {
    return ageOnToday(this.form.get('dateOfBirth')?.value);
  }

  /** Which band rule the current date of birth breaks, if any. */
  get ageError(): 'below' | 'above' | null {
    const e = this.form.get('dateOfBirth')?.errors;
    if (e?.['ageBelow']) return 'below';
    if (e?.['ageAbove']) return 'above';
    return null;
  }

  /** The eligibility verdict shown live under the Age field. */
  get ageMessage(): string | null {
    const r = this.selectedRule;
    const age = this.currentAge;
    if (!r || age == null) return null;

    switch (this.ageError) {
      case 'below':
        return `Too young for this plan — it starts at ${r.ageFrom}.`;
      case 'above':
        return `Too old for this plan — it ends at ${r.ageTo}.`;
      default:
        return `Age ${age} — eligible for this plan.`;
    }
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // 1) FullName = first + middle + last, collapsed to single spaces.
    //    startWith fires once so fullName is initialized immediately.
    const first = this.form.get('firstName');
    const middle = this.form.get('middleName');
    const last = this.form.get('lastName');

    [first, middle, last].forEach((c) => {
      c?.valueChanges
        .pipe(startWith(c.value), takeUntil(this.destroy$))
        .subscribe(() => this.syncFullName());
    });

    // 2) Age derived from dateOfBirth.
    this.form
      .get('dateOfBirth')
      ?.valueChanges.pipe(startWith(this.form.get('dateOfBirth')?.value), takeUntil(this.destroy$))
      .subscribe((dob) => this.syncAge(dob));

    // 3) Keep PAN uppercased.
    this.form
      .get('panNumber')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((v) => this.uppercasePan(v));

    // 4) Pre-fetch the active Terms & Conditions so the "View T&C" dialog is instant.
    if (isPlatformBrowser(this.platformId)) this.loadTerms();
  }

  /** Fetch the active T&C entries and render them as one sanitized HTML block. */
  private loadTerms(): void {
    this.terms.getActive().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const html = list.map((t: any) => `<h3>${t.title ?? ''}</h3>${t.content ?? ''}`).join('<hr/>');
        this.termsHtml.set(
          this.sanitizer.bypassSecurityTrustHtml(html || '<p>No terms &amp; conditions are configured.</p>'),
        );
        this.termsLoaded = true;
      },
    });
  }

  openTerms(): void {
    if (!this.termsLoaded) this.loadTerms();
    this.showTerms.set(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** True once a rule is chosen — gates the premium summary panel. */
  get hasRule(): boolean {
    return this.form.get('premiumWithGST')?.value != null;
  }

  /** "Allowed age: 18–65" hint shown under the Age field once a rule is picked. */
  get selectedRangeHint(): string | null {
    const r = this.selectedRule;
    return r ? `Allowed age: ${r.ageFrom}–${r.ageTo}` : null;
  }

  /** first + ' ' + middle + ' ' + last, trimmed and collapsed to single spaces. */
  private syncFullName(): void {
    const parts = [
      this.form.get('firstName')?.value,
      this.form.get('middleName')?.value,
      this.form.get('lastName')?.value,
    ];
    const full = parts
      .map((p) => (p ?? '').toString().trim())
      .filter((p) => p.length > 0)
      .join(' ');
    // emitEvent:false — fullName is a derived/disabled control, never round-trips.
    this.form.get('fullName')?.setValue(full, { emitEvent: false });
  }

  /** Whole-year age from a Date; clears age when DOB is empty/invalid.
   *  Shares ageOnToday with the band validator so the number shown and the number checked
   *  can never disagree. */
  private syncAge(dob: any): void {
    this.form.get('age')?.setValue(ageOnToday(dob), { emitEvent: false });
  }

  /** Force the PAN control value to upper-case as the user types. */
  private uppercasePan(v: any): void {
    if (typeof v !== 'string') return;
    const upper = v.toUpperCase();
    if (upper !== v) {
      this.form.get('panNumber')?.setValue(upper, { emitEvent: false });
    }
  }
}
